/**
 * FirmwareUpdatePanel - Firmware update interface
 *
 * Allows uploading new firmware to the MasterModule via the bootloader system.
 * Firmware version is parsed from filename (format: *_v{major}.{minor}.{patch}.bin)
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  SerialConnection,
  getSerialConnection,
} from '@/lib/serialConnection';
import {
  FirmwareFileInfo,
  FirmwareStatus,
  InstalledFirmwareInfo,
  readFirmwareFile,
  formatFileSize,
} from '@/lib/firmwareUpdate';

type UploadState = 'idle' | 'uploading' | 'success' | 'error';

export function FirmwareUpdatePanel() {
  const [isConnected, setIsConnected] = useState(false);
  const [selectedFile, setSelectedFile] = useState<FirmwareFileInfo | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [uploadState, setUploadState] = useState<UploadState>('idle');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadMessage, setUploadMessage] = useState('');
  const [installedInfo, setInstalledInfo] = useState<InstalledFirmwareInfo | null>(null);
  const [pendingStatus, setPendingStatus] = useState<FirmwareStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const connectionRef = useRef<SerialConnection | null>(null);

  // Setup connection state tracking
  useEffect(() => {
    const conn = getSerialConnection();
    connectionRef.current = conn;

    const unsubState = conn.onStateChange((state) => {
      const connected = state === 'connected';
      setIsConnected(connected);
      if (connected) {
        refreshStatus();
      } else {
        setInstalledInfo(null);
        setPendingStatus(null);
      }
    });

    return () => {
      unsubState();
    };
  }, []);

  // Refresh firmware status from device
  const refreshStatus = useCallback(async () => {
    const conn = connectionRef.current;
    if (!conn?.isConnected()) return;

    setStatusLoading(true);
    try {
      // Run sequentially — both call sendCommand which shares a single readBuffer
      const installed = await conn.getInstalledFirmwareInfo();
      const pending = await conn.getFirmwareStatus();
      setInstalledInfo(installed);
      setPendingStatus(pending);
    } catch (err) {
      console.error('Failed to get firmware status:', err);
    } finally {
      setStatusLoading(false);
    }
  }, []);

  // Handle file selection
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setFileError(null);
    setSelectedFile(null);
    setUploadState('idle');
    setUploadMessage('');

    if (!file) return;

    try {
      const info = await readFirmwareFile(file);
      setSelectedFile(info);
    } catch (err) {
      setFileError(err instanceof Error ? err.message : 'Failed to read file');
    }
  };

  // Handle firmware upload
  const handleUpload = async () => {
    const conn = connectionRef.current;
    if (!conn?.isConnected() || !selectedFile) return;

    setUploadState('uploading');
    setUploadProgress(0);
    setUploadMessage('Starting upload...');

    try {
      const result = await conn.uploadFirmware(
        selectedFile.data,
        selectedFile.version,
        (percent, bytesReceived) => {
          setUploadProgress(percent);
          if (percent < 100) {
            setUploadMessage(`Uploading: ${formatFileSize(bytesReceived)} / ${formatFileSize(selectedFile.size)}`);
          } else {
            setUploadMessage('Verifying and writing to flash...');
          }
        }
      );

      if (result.success) {
        setUploadState('success');
        setUploadMessage('Firmware uploaded successfully! Use "Apply Update" to install.');
        // Refresh status to show pending update
        await refreshStatus();
      } else {
        setUploadState('error');
        setUploadMessage(result.message || 'Upload failed');
      }
    } catch (err) {
      setUploadState('error');
      setUploadMessage(err instanceof Error ? err.message : 'Upload failed');
    }
  };

  // Handle trigger update (reboot)
  const handleTriggerUpdate = async () => {
    const conn = connectionRef.current;
    if (!conn?.isConnected()) return;

    if (!confirm(
      'This will reboot the device to apply the firmware update.\n\n' +
      'The device will be disconnected during the update process.\n\n' +
      'Continue?'
    )) {
      return;
    }

    try {
      const result = await conn.triggerFirmwareUpdate();
      if (result.success) {
        setUploadMessage('Device is rebooting to apply update. Please reconnect after update completes.');
      } else {
        setUploadMessage(result.message || 'Failed to trigger update');
      }
    } catch (err) {
      setUploadMessage(err instanceof Error ? err.message : 'Failed to trigger update');
    }
  };

  // Handle abort pending update
  const handleAbortUpdate = async () => {
    const conn = connectionRef.current;
    if (!conn?.isConnected()) return;

    if (!confirm('Cancel the pending firmware update?')) {
      return;
    }

    try {
      const result = await conn.abortFirmwareUpdate();
      if (result.success) {
        setUploadMessage('Pending update cancelled.');
        await refreshStatus();
      } else {
        setUploadMessage(result.message || 'Failed to abort update');
      }
    } catch (err) {
      setUploadMessage(err instanceof Error ? err.message : 'Failed to abort update');
    }
  };

  // Clear file selection
  const handleClearFile = () => {
    setSelectedFile(null);
    setFileError(null);
    setUploadState('idle');
    setUploadMessage('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (!isConnected) {
    return (
      <div style={styles.notConnected}>
        Connect to device to manage firmware updates.
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Installed Firmware Info */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <span>Installed Firmware</span>
          <button
            onClick={refreshStatus}
            disabled={statusLoading}
            style={styles.refreshButton}
          >
            {statusLoading ? '...' : 'Refresh'}
          </button>
        </div>
        {installedInfo?.version ? (
          <div style={styles.infoGrid}>
            <span style={styles.infoLabel}>Version:</span>
            <span style={styles.infoValue}>{installedInfo.version.display}</span>
            <span style={styles.infoLabel}>Size:</span>
            <span style={styles.infoValue}>
              {installedInfo.size ? formatFileSize(installedInfo.size) : 'N/A'}
            </span>
            <span style={styles.infoLabel}>Install count:</span>
            <span style={styles.infoValue}>{installedInfo.installCount ?? 'N/A'}</span>
          </div>
        ) : (
          <div style={styles.noInfo}>No firmware info available (first boot?)</div>
        )}
      </div>

      {/* Pending Update Status */}
      {pendingStatus?.hasPendingUpdate && (
        <div style={styles.pendingSection}>
          <div style={styles.sectionHeader}>
            <span>Pending Update</span>
          </div>
          <div style={styles.infoGrid}>
            <span style={styles.infoLabel}>Version:</span>
            <span style={styles.infoValue}>{pendingStatus.version?.display ?? 'Unknown'}</span>
            <span style={styles.infoLabel}>Size:</span>
            <span style={styles.infoValue}>
              {pendingStatus.size ? formatFileSize(pendingStatus.size) : 'N/A'}
            </span>
          </div>
          <div style={styles.pendingActions}>
            <button onClick={handleTriggerUpdate} style={styles.applyButton}>
              Apply Update (Reboot)
            </button>
            <button onClick={handleAbortUpdate} style={styles.abortButton}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* File Selection */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>Upload New Firmware</div>
        <div style={styles.fileInfo}>
          <p style={styles.hint}>
            Firmware filename must include version: <code>*_v1.2.3.bin</code>
          </p>
        </div>

        <div style={styles.fileSelect}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".bin"
            onChange={handleFileSelect}
            style={styles.fileInput}
            disabled={uploadState === 'uploading'}
          />
          {selectedFile && (
            <button
              onClick={handleClearFile}
              style={styles.clearButton}
              disabled={uploadState === 'uploading'}
            >
              Clear
            </button>
          )}
        </div>

        {/* File error */}
        {fileError && (
          <div style={styles.errorBox}>
            {fileError}
          </div>
        )}

        {/* Selected file info */}
        {selectedFile && (
          <div style={styles.selectedFile}>
            <div style={styles.infoGrid}>
              <span style={styles.infoLabel}>File:</span>
              <span style={styles.infoValue}>{selectedFile.name}</span>
              <span style={styles.infoLabel}>Version:</span>
              <span style={styles.infoValue}>{selectedFile.version.display}</span>
              <span style={styles.infoLabel}>Size:</span>
              <span style={styles.infoValue}>{formatFileSize(selectedFile.size)}</span>
            </div>

            {/* Version comparison */}
            {installedInfo?.version && (
              <div style={styles.versionCompare}>
                {selectedFile.version.hex > installedInfo.version.hex ? (
                  <span style={styles.upgradeNote}>
                    ↑ Upgrade from {installedInfo.version.display}
                  </span>
                ) : selectedFile.version.hex < installedInfo.version.hex ? (
                  <span style={styles.downgradeNote}>
                    ↓ Downgrade from {installedInfo.version.display}
                  </span>
                ) : (
                  <span style={styles.sameNote}>
                    = Same version as installed
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Upload progress + warning */}
        {uploadState === 'uploading' && (
          <>
            <div style={styles.uploadWarning}>
              Do not disconnect or close this page during upload.
            </div>
            <div style={styles.progressContainer}>
              <div style={styles.progressBar}>
                <div
                  style={{
                    ...styles.progressFill,
                    width: `${uploadProgress}%`,
                  }}
                />
              </div>
              <div style={styles.progressText}>{uploadProgress}%</div>
            </div>
          </>
        )}

        {/* Upload message */}
        {uploadMessage && (
          <div
            style={{
              ...styles.messageBox,
              backgroundColor:
                uploadState === 'success' ? '#d4edda' :
                uploadState === 'error' ? '#f8d7da' :
                '#e2e3e5',
              color:
                uploadState === 'success' ? '#155724' :
                uploadState === 'error' ? '#721c24' :
                '#383d41',
            }}
          >
            {uploadMessage}
          </div>
        )}

        {/* Upload button */}
        {selectedFile && uploadState !== 'uploading' && (
          <button
            onClick={handleUpload}
            style={styles.uploadButton}
            disabled={pendingStatus?.hasPendingUpdate}
          >
            {pendingStatus?.hasPendingUpdate
              ? 'Clear pending update first'
              : 'Upload Firmware'}
          </button>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  notConnected: {
    padding: '12px',
    backgroundColor: '#fff3cd',
    border: '1px solid #ffeeba',
    borderRadius: '4px',
    color: '#856404',
    fontSize: '12px',
  },
  section: {
    padding: '12px',
    backgroundColor: '#f8f9fa',
    borderRadius: '4px',
    border: '1px solid #dee2e6',
  },
  pendingSection: {
    padding: '12px',
    backgroundColor: '#fff3cd',
    borderRadius: '4px',
    border: '1px solid #ffc107',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontWeight: 'bold',
    fontSize: '13px',
    marginBottom: '10px',
  },
  refreshButton: {
    padding: '2px 8px',
    fontSize: '11px',
    border: '1px solid #ccc',
    borderRadius: '3px',
    backgroundColor: '#fff',
    cursor: 'pointer',
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    gap: '4px 12px',
    fontSize: '12px',
  },
  infoLabel: {
    color: '#666',
  },
  infoValue: {
    fontFamily: 'monospace',
  },
  noInfo: {
    fontSize: '12px',
    color: '#666',
    fontStyle: 'italic',
  },
  pendingActions: {
    display: 'flex',
    gap: '8px',
    marginTop: '12px',
  },
  applyButton: {
    padding: '6px 12px',
    fontSize: '12px',
    fontWeight: 500,
    color: '#fff',
    backgroundColor: '#28a745',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  abortButton: {
    padding: '6px 12px',
    fontSize: '12px',
    fontWeight: 500,
    color: '#fff',
    backgroundColor: '#dc3545',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  fileInfo: {
    marginBottom: '8px',
  },
  hint: {
    fontSize: '11px',
    color: '#666',
    margin: 0,
  },
  fileSelect: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  fileInput: {
    flex: 1,
    fontSize: '12px',
  },
  clearButton: {
    padding: '4px 8px',
    fontSize: '11px',
    border: '1px solid #ccc',
    borderRadius: '3px',
    backgroundColor: '#fff',
    cursor: 'pointer',
  },
  errorBox: {
    marginTop: '8px',
    padding: '8px',
    backgroundColor: '#f8d7da',
    border: '1px solid #f5c6cb',
    borderRadius: '4px',
    color: '#721c24',
    fontSize: '11px',
    whiteSpace: 'pre-wrap',
  },
  selectedFile: {
    marginTop: '12px',
    padding: '10px',
    backgroundColor: '#e8f5e9',
    borderRadius: '4px',
  },
  versionCompare: {
    marginTop: '8px',
    paddingTop: '8px',
    borderTop: '1px solid #c8e6c9',
    fontSize: '12px',
  },
  upgradeNote: {
    color: '#28a745',
    fontWeight: 500,
  },
  downgradeNote: {
    color: '#dc3545',
    fontWeight: 500,
  },
  sameNote: {
    color: '#6c757d',
  },
  progressContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '12px',
  },
  progressBar: {
    flex: 1,
    height: '8px',
    backgroundColor: '#e9ecef',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#007bff',
    transition: 'width 0.2s ease-out',
  },
  progressText: {
    fontSize: '11px',
    fontWeight: 500,
    minWidth: '35px',
    textAlign: 'right',
  },
  uploadWarning: {
    marginTop: '12px',
    padding: '8px 10px',
    backgroundColor: '#fff3cd',
    border: '1px solid #ffc107',
    borderRadius: '4px',
    color: '#856404',
    fontSize: '12px',
    fontWeight: 500,
  },
  messageBox: {
    marginTop: '8px',
    padding: '8px',
    borderRadius: '4px',
    fontSize: '12px',
  },
  uploadButton: {
    marginTop: '12px',
    padding: '8px 16px',
    fontSize: '13px',
    fontWeight: 500,
    color: '#fff',
    backgroundColor: '#007bff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    width: '100%',
  },
} satisfies Record<string, React.CSSProperties>;
